/**
 * @file Formatting and data manipulation helpers.
 */

/**
 * Converts the input string to a string containing only digits.
 *
 * @param input - An input string, which may contain non-digit characters.
 * @returns A string containing only the digit characters from the input, if any.
 */
export function toDigitsOnly(input: string | null | undefined): string {
  return input ? input.replace(/[^\d]/g, "") : "";
}

/**
 * Formats a phone number into a standard phone number format: (XXX) XXX-XXXX.
 *
 * @param inputPhone - The phone number input as a string.
 * @returns The formatted phone number as a string.
 */
export function formatPrettyPhone(inputPhone: string): string {
  const filteredInput = toDigitsOnly(inputPhone);
  const reTelStr = /(?:(?<areacode>\d{0,3})(?:(?<prefix>\d{0,3})(?<linenumber>\d{0,4})?)?)?/g;
  const result = reTelStr.exec(filteredInput);

  if (!result?.groups) {
    return `${filteredInput.replace(/[^\d]/g, "")}`;
  }

  const { areacode = "", prefix = "", linenumber = "" } = result.groups;

  if (!prefix) {
    return `${areacode}`;
  } else if (!linenumber) {
    return `(${areacode}) ${prefix}`;
  }

  return `(${areacode}) ${prefix}-${linenumber}`;
}

/**
 * Converts a given string to a reasonably faithful ASCII representation suitable for use in a QR code's data URL.
 * - Replaces accented characters with their non-accented counterparts.
 * - Normalizes the string to Unicode Normalization Form KD.
 * - Removes any combining diacritical marks.
 * - Replaces any whitespace characters with a single space.
 * - Removes any non-word and non-whitespace characters.
 *
 * NOTE: This function doesn't handle hyphenated names very well. It could be revisited.
 *
 * @param str - The input string to be converted.
 * @returns The ASCII representation of the input string.
 */
export function toASCII(str: string | null | undefined): string {
  if (str === null || str === undefined) return "";

  // prettier-ignore
  const accentMap: Record<string, string> = {
    'Š': 'S',  'Œ': 'OE', 'Ž': 'Z',  'š': 's',  'œ': 'oe', 'ž': 'z',  'Ÿ': 'Y',  '¥': 'Y',  'µ': 'u',
    'À': 'A',  'Á': 'A',  'Â': 'A',  'Ã': 'A',  'Ä': 'A',  'Å': 'A',  'Æ': 'AE', 'Ç': 'C',  'È': 'E',
    'É': 'E',  'Ê': 'E',  'Ë': 'E',  'Ì': 'I',  'Í': 'I',  'Î': 'I',  'Ï': 'I',  'Ð': 'D',  'Ñ': 'N',
    'Ò': 'O',  'Ó': 'O',  'Ô': 'O',  'Õ': 'O',  'Ö': 'O',  'Ø': 'O',  'Ù': 'U',  'Ú': 'U',  'Û': 'U',
    'Ü': 'U',  'Ý': 'Y',  'Þ': 'TH', 'ß': 'ss', 'à': 'a',  'á': 'a',  'â': 'a',  'ã': 'a',  'ä': 'a',
    'å': 'a',  'æ': 'ae', 'ç': 'c',  'è': 'e',  'é': 'e',  'ê': 'e',  'ë': 'e',  'ì': 'i',  'í': 'i',
    'î': 'i',  'ï': 'i',  'ð': 'd',  'ñ': 'n',  'ò': 'o',  'ó': 'o',  'ô': 'o',  'õ': 'o',  'ö': 'o',
    'ø': 'o',  'ù': 'u',  'ú': 'u',  'û': 'u',  'ü': 'u',  'ý': 'y',  'þ': 'th', 'ÿ': 'y',  'Ŕ': 'R',
    'ŕ': 'r',  'ƒ': 'f'
  };

  str = str.replace(/./g, (char) => accentMap[char] || char);
  str = str.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  str = str.replace(/[\s]/g, " ");
  str = str.replace(/[^\w\s]/g, "");

  return str;
}

/**
 * Parses an error response to extract a user-friendly message.
 *
 * @param errorResponse - The error response object which may contain a message.
 * @param defaultMsg - The default message to use if the error response does not contain a message.
 * @returns A user-friendly message extracted from the error response or the default message.
 */
export function parseUserFriendlyMessage(
  errorResponse: { message: string } | undefined,
  defaultMsg: string = ""
): string {
  return errorResponse?.message || defaultMsg;
}

/**
 * Converts an integer to its ordinal representation as a string.
 *
 * @param {number | string} number - The integer to be converted. Can be a number or a string that represents a number.
 * @returns {string} The ordinal representation of the integer. Returns an empty string if the input is not a positive integer.
 *
 * @example
 * ```typescript
 * integerToOrdinal(1); // "1st"
 * integerToOrdinal(2); // "2nd"
 * integerToOrdinal(3); // "3rd"
 * integerToOrdinal(4); // "4th"
 * integerToOrdinal("21"); // "21st"
 * integerToOrdinal("100"); // "100th"
 * integerToOrdinal("abc"); // ""
 * ```
 */
export function integerToOrdinal(number: number | string): string {
  const intNumber = Number(number);

  if (!Number.isInteger(intNumber) || intNumber < 1) {
    return "";
  }

  const suffixes = ["th", "st", "nd", "rd"];
  const remainder10 = intNumber % 10;
  const remainder100 = intNumber % 100;

  const suffix =
    remainder10 <= 3 && remainder100 !== 11 && remainder100 !== 12 && remainder100 !== 13
      ? suffixes[remainder10]
      : suffixes[0];

  return intNumber + suffix;
}
